<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/modal/PosModalCovers.vue.
  // Opened via showDynamicDialog<IPosV2CoversResult | null>(..., 'pos-covers').
  import { computed, getCurrentInstance, ref } from 'vue';
  import { formatTableDisplayName } from '@/domain/pos/order-formatting';
  import { useModal } from '@/composition/use-modal';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  export interface IPosV2CoversResult {
    /** Chosen party size, or null when explicitly skipped/cleared. */
    covers: number | null;
  }

  const props = withDefaults(
    defineProps<{
      tableName?: string | null;
      /** Customer-suggested party size (coversSuggested) — prefills the input. */
      suggested?: number | null;
      /** Current value when editing an already-set covers count. */
      initial?: number | null;
      skipLabel?: string;
      /** Branch coversPromptMode 'required': hides the skip button. */
      required?: boolean;
    }>(),
    {
      tableName: null,
      suggested: null,
      initial: null,
      skipLabel: undefined,
      required: false,
    }
  );

  const modals = useModal();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const effectiveSkipLabel = computed(
    () => props.skipLabel ?? i18nT('posV2.skip')
  );

  const MAX_COVERS = 200;
  const QUICK_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  const inputValue = ref(
    props.initial != null
      ? String(props.initial)
      : props.suggested != null
      ? String(props.suggested)
      : ''
  );

  const parsedInput = computed<number | null>(() => {
    const parsed = Number.parseInt(inputValue.value, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return null;
    return Math.min(MAX_COVERS, parsed);
  });

  const displayTableName = computed(() =>
    props.tableName
      ? formatTableDisplayName(props.tableName, i18nT('posV2.tableWord'))
      : null
  );

  const title = computed(() =>
    displayTableName.value
      ? i18nT('posV2.coversTitleWithTable', { table: displayTableName.value })
      : i18nT('posV2.coversTitle')
  );

  function submit(result: IPosV2CoversResult) {
    modals.dialogSubmit('pos-covers', result);
  }
</script>

<template>
  <div class="pos-v2-covers-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <span class="modal-subtitle">{{ $t('posV2.coversSubtitle') }}</span>
    </div>

    <div class="covers-body">
      <div v-if="suggested != null" class="suggested-hint">
        <PosUiIcon name="lightbulb-outline" class="hint-icon" />
        <span>{{ $t('posV2.coversSuggested', { count: suggested }) }}</span>
      </div>

      <div class="quick-grid">
        <PosUiButton
          v-for="n in QUICK_OPTIONS"
          :key="n"
          color="neutral"
          variant="outline"
          size="xl"
          class="quick-button"
          @click="submit({ covers: n })"
        >
          {{ n }}
        </PosUiButton>
      </div>

      <PosUiInput
        :value="inputValue"
        type="number"
        inputmode="numeric"
        :placeholder="$t('posV2.coversOtherPlaceholder')"
        size="lg"
        icon="account-group"
        class="covers-input"
        @input="(v) => (inputValue = v)"
        @enter="parsedInput != null && submit({ covers: parsedInput })"
      />
    </div>

    <div class="modal-footer" :class="{ 'modal-footer--single': required }">
      <PosUiButton
        v-if="!required"
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-skip"
        @click="submit({ covers: null })"
      >
        {{ effectiveSkipLabel }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        variant="solid"
        size="lg"
        class="btn-confirm"
        :disabled="parsedInput == null"
        @click="submit({ covers: parsedInput })"
      >
        {{ $t('posV2.confirm') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-covers-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .covers-body {
      @apply tw-flex tw-flex-col tw-gap-4;
    }

    .suggested-hint {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-800;

      .hint-icon {
        @apply tw-flex-shrink-0 tw-text-base;
      }
    }

    .quick-grid {
      @apply tw-grid tw-grid-cols-3 tw-gap-2;

      :deep(.quick-button) {
        @apply tw-min-h-[56px] tw-justify-center tw-text-lg tw-font-semibold;
      }
    }

    .covers-input {
      @apply tw-w-full;
    }

    .modal-footer {
      @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2;

      &--single {
        @apply tw-justify-end;
      }

      :deep(.btn-skip),
      :deep(.btn-confirm) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
